<template>
  <div>
    <b-row>
      <b-col class="mb-3" cols="12">
        <h1>Ставки по заявкам</h1>
      </b-col>
    </b-row>

    <!-- Фильтры -->
    <b-card class="mb-3">
      <b-card-body class="py-2">
        <b-row class="align-items-end">
          <!-- Период -->
          <b-col md="3">
            <CustomDropdown
              v-model="filters.month"
              :options="monthOptions"
              label="Период"
              placeholder="Выберите период"
              @change="onFiltersChange"
            />
          </b-col>

          <!-- Отдел -->
          <b-col md="3" v-if="!isToManager && !isLogisticsManager && !isLogisticsMultimManager">
            <CustomDropdown
              v-model="filters.department_id"
              :options="departmentOptions"
              label="Отдел"
              placeholder="Выберите отдел"
              null-text="Все отделы"
              :allow-null="true"
              @change="onDepartmentChange"
            />
          </b-col>

          <!-- Пользователь -->
          <b-col md="3" v-if="!isToManager && !isLogisticsManager && !isLogisticsMultimManager">
            <CustomDropdown
              v-model="filters.user_id"
              :options="userOptions"
              label="Сотрудник"
              placeholder="Все сотрудники"
              null-text="Все сотрудники отдела"
              :allow-null="true"
              @change="onFiltersChange"
            />
          </b-col>

          <!-- Индикатор загрузки -->
          <b-col md="3" class="d-flex align-items-center mb-2">
            <b-spinner v-if="loading" small class="mr-2" />
            <small v-if="loading" class="text-muted">Загрузка...</small>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Результаты -->
    <b-card v-if="reportData.length > 0">
      <b-card-header class="py-2">
        <h6 class="mb-0">Результаты отчета</h6>
      </b-card-header>
      <b-card-body class="py-2">
        <!-- Таблица результатов -->
        <b-table
          :items="reportData"
          :fields="filteredTableFields"
          striped
          hover
          responsive
          class="mb-4"
          small
        >
          <template #cell(order_id)="row">
            <a 
              href="#" 
              @click.prevent="openOrderSidebar(row.item.order_id)"
              class="text-primary"
            >
              {{ row.item.order_id }}
            </a>
          </template>
          <template #cell(delivery_cost)="row">
            <div v-if="row.item.delivery_cost" class="d-flex align-items-center">
              <span 
                class="font-weight-bold"
                :class="{ 
                  'clickable-rate': canEditRates,
                  'text-danger': hasRateHistory(row.item, 'delivery_cost')
                }"
                @click="canEditRates ? openEditRateModal(row.item, 'delivery_cost') : null"
                :title="canEditRates ? 'Нажмите для редактирования' : ''"
              >
                {{ formatCurrency(row.item.delivery_cost, row.item.currency_id) }}
              </span>
              <i 
                v-if="hasRateHistory(row.item, 'delivery_cost')"
                class="fas fa-question-circle text-secondary ml-1"
                :title="getChangeReason(row.item, 'delivery_cost')"
                style="font-size: 12px; cursor: help;"
              ></i>
            </div>
            <span v-else class="text-danger">Не установлено</span>
          </template>
          <template #cell(real_rate)="row">
            <span v-if="row.item.real_rate" class="font-weight-bold">
              {{ formatCurrency(row.item.real_rate, row.item.real_rate_currency_code) }}
            </span>
            <span v-else class="text-muted">Не установлено</span>
          </template>
          <template #cell(client_rate)="row">
            <span v-if="row.item.client_rate" class="font-weight-bold">
              {{ formatCurrency(row.item.client_rate, row.item.currency_id) }}
            </span>
            <span v-else class="text-danger">Не установлено</span>
          </template>
          <template #cell(delivery_cost_rate_sent_at)="row">
            <span v-if="row.item.delivery_cost_rate_sent_at">
              {{ formatDate(row.item.delivery_cost_rate_sent_at) }}
            </span>
            <span v-else class="text-muted">Не известно</span>
          </template>
          <template #cell(client_rate_manager_name)="row">
            <span v-if="row.item.client_rate_manager_name && row.item.client_rate_manager_name !== 'Не указан'">
              {{ row.item.client_rate_manager_name }}
            </span>
            <span v-else class="text-muted">Не известно</span>
          </template>
          <template #cell(client_rate_saved_at)="row">
            <span v-if="row.item.client_rate_saved_at">
              {{ formatDate(row.item.client_rate_saved_at) }}
            </span>
            <span v-else class="text-muted">Не известно</span>
          </template>
        </b-table>

        <!-- Итоги -->
        <b-alert variant="info" show class="py-2">
          <div class="d-flex justify-content-between align-items-center">
            <span class="font-weight-bold">Итоги:</span>
            <div>
              <span class="mr-3">Запросов: <strong>{{ summary.total_records }}</strong></span>
              <span class="mr-3">Ставок перевозчика: <strong>{{ summary.total_delivery_costs }}</strong></span>
              <span v-if="!isLogisticsManager && !isLogisticsHeadManager && !isLogisticsMultimManager && !isLogisticsMultimHeadManager">Ставок для клиента: <strong>{{ summary.total_client_rates }}</strong></span>
            </div>
          </div>
        </b-alert>
      </b-card-body>
    </b-card>

    <!-- Сообщение об отсутствии данных -->
    <b-alert v-else-if="!loading && reportData.length === 0" variant="warning" show>
      По выбранным критериям данные не найдены.
    </b-alert>

    <!-- Модальное окно для редактирования ставки -->
    <b-modal
      id="edit-rate-modal"
      ref="editRateModal"
      title="Редактирование ставки"
      size="md"
      @show="onEditRateModalShow"
      @hidden="onEditRateModalHidden"
    >
      <b-form @submit.prevent="saveRateChange">
        <b-form-group label="Тип ставки">
          <b-form-input
            :value="editRateData.fieldName"
            readonly
            class="bg-light"
          />
        </b-form-group>

        <b-form-group label="Текущее значение">
          <b-form-input
            :value="editRateData.currentValue && editRateData.currentValue > 0 ? formatCurrency(editRateData.currentValue, editRateData.currencyCode) : 'Не установлено'"
            readonly
            class="bg-light"
          />
        </b-form-group>

        <b-form-group label="Новое значение" :invalid-feedback="newValueError">
          <b-form-input
            v-model="editRateData.newValue"
            type="number"
            step="0.01"
            min="0"
            :state="newValueState"
            placeholder="Введите новое значение"
            required
          />
        </b-form-group>

        <b-form-group label="Причина изменения" :invalid-feedback="reasonError">
          <b-form-textarea
            v-model="editRateData.changeReason"
            rows="3"
            :state="reasonState"
            placeholder="Обязательно укажите причину изменения ставки"
            required
          />
        </b-form-group>

        <!-- История изменений -->
        <b-form-group label="История изменений" v-if="editRateData.rateHistory && editRateData.rateHistory.length > 0">
          <div class="rate-history">
            <div 
              v-for="(history, index) in editRateData.rateHistory" 
              :key="index"
              class="rate-history-item"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div class="d-flex align-items-center">
                  <span style="min-width: 120px;"><strong>{{ formatDate(history.changed_at) }}</strong></span>
                  <span class="text-muted lh14px ml-2">{{ history.change_reason }}</span>
                </div>
                <div class="text-muted text-right small" style="min-width: 150px;">
                  {{ formatCurrency(history.old_value || 0, editRateData.currencyCode) }} → 
                  {{ formatCurrency(history.new_value || 0, editRateData.currencyCode) }}
                </div>
              </div>
            </div>
          </div>
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <b-button variant="secondary" @click="cancelRateEdit">
          Отмена
        </b-button>
        <b-button 
          variant="primary" 
          @click="saveRateChange"
          :disabled="!canSaveRate"
        >
          Сохранить
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import { openOrderSidebar } from "@/mixins/openOrderSidebar";
import CustomDropdown from "@/components/CustomDropdown.vue";
import { 
  API_QUOTATION_RATES_DATA, 
  API_QUOTATION_RATES_USERS,
  API_QUOTATION_RATES_DEPARTMENTS,
  API_QUOTATION_RATES_FILTER_DATA,
  API_QUOTATION_RATES_UPDATE_RATE
} from "@/constants";

export default {
  name: "QuotationRatesReport",
  
  components: {
    CustomDropdown
  },
  
  mixins: [openOrderSidebar],

  data() {
    return {
      loading: false,
      loadTimeout: null,
      reportData: [],
      summary: {
        total_records: 0,
        total_delivery_costs: 0,
        total_client_rates: 0
      },
      filters: {
        month: null,
        department_id: null,
        user_id: null
      },
      userOptions: [],
      allUsers: [], // Все пользователи для фильтрации на фронтенде
      departmentOptions: [],
      monthOptions: [],
      // Данные для редактирования ставок
      editRateData: {
        quotationAnswerId: null,
        fieldName: '',
        fieldType: '', // 'delivery_cost' или 'discount_rate'
        currentValue: null,
        newValue: null,
        changeReason: '',
        currencyCode: 'RUB',
        rateHistory: []
      }
    };
  },

  computed: {
    ...mapGetters("auth", ["currentUser", "statusUser", "headDepartment", "roleNoHead"]),
    
    maxDate() {
      return new Date().toISOString().split('T')[0];
    },

    isToManager() {
      return this.currentUser.role.includes("TO-manager");
    },

    isToHeadManager() {
      return this.currentUser.role.includes("TO-head-manager");
    },

    isLogisticsManager() {
      return this.currentUser.role.includes("logistics-manager");
    },

    isLogisticsHeadManager() {
      return this.currentUser.role.includes("logistics-head-manager");
    },

    isLogisticsMultimManager() {
      return this.currentUser.role.includes("logistics-multim-manager");
    },

    isLogisticsMultimHeadManager() {
      return this.currentUser.role.includes("logistics-multim-head-manager");
    },
    // Фильтруем колонки таблицы в зависимости от роли пользователя
    filteredTableFields() {
      const allFields = [
        { key: 'order_id', label: 'Заказ', sortable: true },
        { key: 'service_name', label: 'Вид услуги', sortable: true },
        { key: 'user_name', label: 'Ставку перевозчика разместил(а)', sortable: true, thClass: 'width180px' },
        { key: 'delivery_cost', label: 'Ставка перевозчика', sortable: true, thClass: 'width180px' },
        { key: 'real_rate', label: 'Реальная ставка', sortable: true, thClass: 'width180px' },
        { key: 'delivery_cost_rate_sent_at', label: 'Дата отправки ставки перевозчика', sortable: true },
        { key: 'client_rate_manager_name', label: 'Ставку для клиента разместил(а)', sortable: true, thClass: 'width180px' },
        { key: 'client_rate', label: 'Ставка для клиента', sortable: true, thClass: 'width180px' },
        { key: 'client_rate_saved_at', label: 'Дата размещения ставки для клиента', sortable: true }
      ];

      // Если пользователь TO-менеджер, скрываем колонку реальной ставки
      if (this.isToManager || this.isToHeadManager) {
        return allFields.filter(field => 
          !['real_rate'].includes(field.key)
        );
      }

      // Если пользователь логистический менеджер, скрываем колонки со ставками для клиента
      if (this.isLogisticsManager || this.isLogisticsHeadManager || 
          this.isLogisticsMultimManager || this.isLogisticsMultimHeadManager) {
        return allFields.filter(field => 
          !['client_rate'].includes(field.key)
        );
      }

      return allFields;
    },

    // Валидация для редактирования ставок
    newValueState() {
      if (!this.editRateData.newValue) return null;
      return this.editRateData.newValue > 0 ? true : false;
    },

    newValueError() {
      if (!this.editRateData.newValue) return 'Введите новое значение';
      return this.editRateData.newValue <= 0 ? 'Значение должно быть больше 0' : '';
    },

    reasonState() {
      return this.editRateData.changeReason.length > 0 ? true : false;
    },

    reasonError() {
      return this.editRateData.changeReason.length === 0 ? 'Укажите причину изменения' : '';
    },

    canSaveRate() {
      return this.editRateData.newValue > 0 && 
             this.editRateData.changeReason.length > 0 &&
             parseFloat(this.editRateData.newValue) !== parseFloat(this.editRateData.currentValue || 0);
    },

    canEditRates() {
      return this.isLogisticsManager || this.isLogisticsHeadManager || 
             this.isLogisticsMultimManager || this.isLogisticsMultimHeadManager;
    },

    // Методы для работы с историей изменений ставок
    hasRateHistory() {
      return (item, fieldType) => {
        if (!item.rate_history || !Array.isArray(item.rate_history)) {
          return false;
        }
        // Проверяем, есть ли запись с change_reason для конкретного поля
        return item.rate_history.some(history => 
          history.field_changed === fieldType && 
          history.change_reason && 
          history.change_reason.trim() !== ''
        );
      };
    },

    getRateTooltip() {
      return (item, fieldType) => {
        if (!item.rate_history || !Array.isArray(item.rate_history)) {
          return this.canEditRates ? 'Нажмите для редактирования' : '';
        }
        
        // Фильтруем записи для конкретного поля с change_reason
        const relevantHistory = item.rate_history.filter(h => 
          h.field_changed === fieldType && 
          h.change_reason && 
          h.change_reason.trim() !== ''
        );
        
        if (relevantHistory.length === 0) {
          return this.canEditRates ? 'Нажмите для редактирования' : '';
        }
        
        // Сортируем по дате изменения (новые сверху) и берем последнюю запись
        const sortedHistory = relevantHistory.sort((a, b) => {
          const dateA = new Date(a.changed_at || a.created_at);
          const dateB = new Date(b.changed_at || b.created_at);
          return dateB - dateA; // Сортировка по убыванию (новые сверху)
        });
        
        return `Причина изменения: ${sortedHistory[0].change_reason}`;
      };
    },

    // Получение причины изменения для иконки
    getChangeReason() {
      return (item, fieldType) => {
        if (!item.rate_history || !Array.isArray(item.rate_history)) {
          return '';
        }
        
        // Фильтруем записи для конкретного поля с change_reason
        const relevantHistory = item.rate_history.filter(h => 
          h.field_changed === fieldType && 
          h.change_reason && 
          h.change_reason.trim() !== ''
        );
        
        if (relevantHistory.length === 0) {
          return '';
        }
        
        // Сортируем по дате изменения (новые сверху) и берем последнюю запись
        const sortedHistory = relevantHistory.sort((a, b) => {
          const dateA = new Date(a.changed_at || a.created_at);
          const dateB = new Date(b.changed_at || b.created_at);
          return dateB - dateA; // Сортировка по убыванию (новые сверху)
        });
        
        return sortedHistory[0].change_reason;
      };
    },

    // Получение истории изменений для конкретного поля
    getFieldHistory() {
      return (item, fieldType) => {
        if (!item.rate_history || !Array.isArray(item.rate_history)) {
          return [];
        }
        
        // Фильтруем записи для конкретного поля с change_reason
        const relevantHistory = item.rate_history.filter(h => 
          h.field_changed === fieldType && 
          h.change_reason && 
          h.change_reason.trim() !== ''
        );
        
        if (relevantHistory.length === 0) {
          return [];
        }
        
        // Сортируем по дате изменения (новые сверху)
        return relevantHistory.sort((a, b) => {
          const dateA = new Date(a.changed_at || a.created_at);
          const dateB = new Date(b.changed_at || b.created_at);
          return dateB - dateA; // Сортировка по убыванию (новые сверху)
        });
      };
    }
  },

  async created() {
    // Генерируем опции месяцев
    this.generateMonthOptions();
    
    // Устанавливаем текущий месяц по умолчанию
    const now = new Date();
    this.filters.month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    
    // Загружаем все данные фильтров одним запросом
    await this.loadFilterData();
    
    // Если пользователь TO, устанавливаем его по умолчанию
    if (this.currentUser.role.includes("TO-manager") ||
        this.currentUser.role.includes("TO-head-manager")) {
      this.filters.user_id = this.currentUser.id;
      // Устанавливаем имя пользователя в поле поиска
      this.userSearchQuery = this.currentUser.name;
    }
    
    // Автоматически загружаем данные после установки фильтров
    this.loadReportData();
  },

  methods: {
    generateMonthOptions() {
      const options = [];
      const now = new Date();
      
      // Генерируем последние 24 месяца
      for (let i = 0; i < 24; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const monthName = date.toLocaleDateString('ru', { month: 'long' });
        
        // Делаем первую букву заглавной
        const capitalizedMonthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
        
        options.push({
          value: `${year}-${month}`,
          text: `${capitalizedMonthName} ${year}`
        });
      }
      
      this.monthOptions = options;
    },

    async loadFilterData() {
      try {
        const response = await axios.get(API_QUOTATION_RATES_FILTER_DATA);
        if (response.data.success) {
          const data = response.data.data;
          
          // Загружаем отделы
          this.departmentOptions = data.departments.map(dept => ({
            value: dept.id,
            text: dept.name
          }));
          
          // Сохраняем всех пользователей для фильтрации
          this.allUsers = data.users;
          
          // Загружаем пользователей
          this.userOptions = data.users.map(user => ({
            value: user.id,
            text: user.name
          }));
          
          // Инициализируем отфильтрованные опции
          this.filteredUserOptions = this.userOptions;
        }
      } catch (error) {
        console.error('Ошибка загрузки данных фильтров:', error);
        this.$bvToast.toast('Ошибка загрузки данных фильтров', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },

    async loadDepartments() {
      try {
        const response = await axios.get(API_QUOTATION_RATES_DEPARTMENTS);
        if (response.data.success) {
          // Фильтруем только нужные отделы: 1, 2, 3, 10, 11
          const allowedDepartmentIds = [1, 2, 3, 10, 11];
          this.departmentOptions = response.data.data
            .filter(dept => allowedDepartmentIds.includes(dept.id))
            .map(dept => ({
              value: dept.id,
              text: dept.name
            }));
        }
      } catch (error) {
        console.error('Ошибка загрузки отделов:', error);
        this.$bvToast.toast('Ошибка загрузки отделов', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },

    async loadUsers() {
      try {
        const params = {};
        if (this.filters.department_id) {
          params.department_id = this.filters.department_id;
        }
        
        const response = await axios.get(API_QUOTATION_RATES_USERS, { params });
        if (response.data.success) {
          this.userOptions = response.data.data.map(user => ({
            value: user.id,
            text: user.name
          }));
        }
      } catch (error) {
        console.error('Ошибка загрузки пользователей:', error);
        this.$bvToast.toast('Ошибка загрузки пользователей', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },

    onDepartmentChange() {
      // Сбрасываем выбор пользователя при смене отдела
      this.filters.user_id = null;
      // Фильтруем пользователей по выбранному отделу на фронтенде
      this.filterUsersByDepartment();
      // Перезагружаем данные
      this.onFiltersChange();
    },

    filterUsersByDepartment() {
      if (!this.filters.department_id) {
        // Если отдел не выбран, показываем всех пользователей
        this.userOptions = this.allUsers.map(user => ({
          value: user.id,
          text: user.name
        }));
      } else {
        // Фильтруем пользователей по отделу
        this.userOptions = this.allUsers
          .filter(user => {
            // Проверяем, есть ли у пользователя роли, связанные с выбранным отделом
            return user.roles && user.roles.some(role => 
              role.departaments && role.departaments.some(dept => 
                dept.id === this.filters.department_id
              )
            );
          })
          .map(user => ({
            value: user.id,
            text: user.name
          }));
      }
    },

    async loadReportData() {
      if (!this.filters.month) {
        this.$bvToast.toast('Выберите период', {
          title: 'Предупреждение',
          variant: 'warning',
          solid: true
        });
        return;
      }

      this.loading = true;
      
      try {
        // Парсим формат YYYY-MM
        const [year, month] = this.filters.month.split('-');
        const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        const endDate = new Date(parseInt(year), parseInt(month), 0);
        
        const params = {
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        };

        // Если выбран отдел, добавляем его в параметры
        if (this.filters.department_id) {
          params.department_id = this.filters.department_id;
        }

        if (this.filters.user_id) {
          params.user_ids = [this.filters.user_id];
        }


        const response = await axios.get(API_QUOTATION_RATES_DATA, { params });
        
        if (response.data.success) {
          this.reportData = response.data.data;
          this.summary = response.data.summary;
        } else {
          this.$bvToast.toast('Ошибка загрузки данных отчета', {
            title: 'Ошибка',
            variant: 'danger',
            solid: true
          });
        }
      } catch (error) {
        console.error('Ошибка загрузки отчета:', error);
        this.$bvToast.toast('Ошибка загрузки данных отчета', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },

    onFiltersChange() {
      // Автоматически перезагружаем данные при изменении фильтров с небольшой задержкой
      if (this.loadTimeout) {
        clearTimeout(this.loadTimeout);
      }
      this.loadTimeout = setTimeout(() => {
        this.loadReportData();
      }, 300);
    },

    formatCurrency(amount, currency) {
      if (!amount) return '-';
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: currency || 'RUB',
        minimumFractionDigits: 0
      }).format(amount);
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    // Методы для редактирования ставок
    openEditRateModal(item, fieldType) {

      this.editRateData.quotationAnswerId = item.id;
      this.editRateData.fieldType = fieldType;
      this.editRateData.currentValue = item[fieldType] || 0;
      this.editRateData.newValue = item[fieldType] || 0;
      this.editRateData.changeReason = '';
      this.editRateData.currencyCode = item.currency_id || 'RUB';

      // Загружаем историю изменений для конкретного поля
      this.editRateData.rateHistory = this.getFieldHistory(item, fieldType);

      // Определяем название поля
      const fieldNames = {
        'delivery_cost': 'Ставка перевозчика',
        'client_rate': 'Ставка для клиента',
        'discount_rate': 'Скидка'
      };
      this.editRateData.fieldName = fieldNames[fieldType] || fieldType;

      this.$refs.editRateModal.show();
    },

    onEditRateModalShow() {
      // Фокусируемся на поле нового значения
      this.$nextTick(() => {
        try {
          const modal = this.$refs.editRateModal;
          if (modal) {
            // Попробуем разные способы доступа к DOM
            let modalEl = modal.$el || modal;
            if (modalEl && typeof modalEl.querySelector === 'function') {
              const input = modalEl.querySelector('input[type="number"]');
              if (input) {
                input.focus();
                input.select();
              }
            }
          }
        } catch (error) {
          console.warn('Could not focus input:', error);
        }
      });
    },

    onEditRateModalHidden() {
      // Сбрасываем данные
      this.editRateData = {
        quotationAnswerId: null,
        fieldName: '',
        fieldType: '',
        currentValue: null,
        newValue: null,
        changeReason: '',
        currencyCode: 'RUB',
        rateHistory: []
      };
    },

    cancelRateEdit() {
      this.$refs.editRateModal.hide();
    },

    async saveRateChange() {
      if (!this.canSaveRate) return;

      try {
        const response = await axios.put(`${API_QUOTATION_RATES_UPDATE_RATE}/${this.editRateData.quotationAnswerId}/update-rate`, {
          field_type: this.editRateData.fieldType,
          new_value: parseFloat(this.editRateData.newValue),
          change_reason: this.editRateData.changeReason
        });

        if (response.data.success) {
          this.$bvToast.toast('Ставка успешно обновлена', {
            title: 'Успех',
            variant: 'success',
            solid: true
          });

          // Обновляем данные в таблице
          const item = this.reportData.find(item => item.id === this.editRateData.quotationAnswerId);
          if (item) {
            item[this.editRateData.fieldType] = parseFloat(this.editRateData.newValue);
            
            // Обновляем историю изменений
            if (!item.rate_history) {
              item.rate_history = [];
            }
            
            // Добавляем новую запись в историю
            const newHistoryRecord = {
              field_changed: this.editRateData.fieldType,
              change_reason: this.editRateData.changeReason,
              old_value: this.editRateData.currentValue,
              new_value: parseFloat(this.editRateData.newValue),
              changed_at: new Date().toISOString(),
              created_at: new Date().toISOString()
            };
            
            item.rate_history.unshift(newHistoryRecord); // Добавляем в начало массива
            
            // Обновляем историю в модальном окне
            this.editRateData.rateHistory = this.getFieldHistory(item, this.editRateData.fieldType);
          }

          this.$refs.editRateModal.hide();
        } else {
          throw new Error(response.data.message || 'Ошибка обновления ставки');
        }
      } catch (error) {
        console.error('Ошибка обновления ставки:', error);
        this.$bvToast.toast('Ошибка обновления ставки: ' + (error.response?.data?.message || error.message), {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    }
  },

  beforeDestroy() {
    // Очищаем таймаут при уничтожении компонента
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
    }
  }
};
</script>

<style scoped>
.card {
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.card-body {
  padding: 0.75rem;
}

.table th {
  background-color: #f8f9fa;
  border-top: none;
  font-size: 0.875rem;
}

.table td {
  font-size: 0.875rem;
}

.alert ul {
  margin-bottom: 0;
}

label {
  font-size: 0.875rem;
  font-weight: 500;
}

h1 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

h5 {
  font-size: 1rem;
}

.clickable-rate {
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 3px;
  padding: 2px 4px;
}

.clickable-rate:hover {
  background-color: #e3f2fd;
  color: #1976d2;
  text-decoration: underline;
}

/* Стили для истории изменений */
.rate-history {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 10px;
  background-color: #f8f9fa;
}

.rate-history::-webkit-scrollbar {
    width: 4px;
    height: 13px;
    background-color: #e1e1e1;
    border-radius: 4px !important;
}

.rate-history::-webkit-scrollbar-thumb {
    background-color: #5a6268;
    border-radius: 4px !important;
}

.rate-history::-webkit-scrollbar-track {
    background-color: #e1e1e1;
    border-radius: 4px !important;
}

.rate-history-item {
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
}

.rate-history-item:last-child {
  border-bottom: none;
}

.rate-history-item .text-muted {
  font-size: 0.85rem;
}
</style>
